import {Box, Checkbox, createStyles, Divider, Radio, Stack, Tooltip} from '@mantine/core';
import React, {useCallback} from 'react';

import FolderSelector from 'file-browser/FolderSelector';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import SelectableRadioCard from './eventTriggerConfigForm/SelectableRadioCard';
import RoutineFieldHeader from './RoutineFieldHeader';
import {AgentApprovalCategory} from './RoutineTypes';
import {AccessLevel, deriveAccessLevel} from './useRoutineForm';

const FULL_ACCESS_LOCKED_TOOLTIP = 'An admin needs to grant access before this mode can be used';

interface RoutineAccessSectionProps {
  allowedApprovalCategories: Array<AgentApprovalCategory>;
  agentWorkspaceFolderId: string | null;
  // When false, the Full actions card is shown but locked/disabled.
  fullAccessEnabled: boolean;
  onAccessLevelChange: (level: AccessLevel) => void;
  onAgentWorkspaceFolderChange: (folderId: string | null) => void;
}

const useStyles = createStyles((theme) => ({
  card: {
    cursor: 'pointer',
  },
  selectedCard: {
    backgroundColor: theme.colors.blue[0],
    borderColor: theme.colors.blue[7],
  },
  lockIcon: {
    display: 'flex',
    alignItems: 'center',
    // Grey (not the default near-black) to read as a passive/disabled affordance.
    color: theme.colors.gray[6],
  },
  folderSelectorWrapper: {
    marginLeft: theme.spacing.lg,
    maxWidth: 400,
  },
}));

export default function RoutineAccessSection({
  allowedApprovalCategories,
  agentWorkspaceFolderId,
  fullAccessEnabled,
  onAccessLevelChange,
  onAgentWorkspaceFolderChange,
}: RoutineAccessSectionProps): JSX.Element {
  const {classes, cx} = useStyles();

  // "Full actions" covers both unrestricted full access ('full') and full access limited to a folder
  // ('targeted'); the checkbox toggles between them. See useRoutineForm for the primitive mapping.
  const selectedLevel = deriveAccessLevel(allowedApprovalCategories, agentWorkspaceFolderId);
  const isFullSelected = selectedLevel === 'full' || selectedLevel === 'targeted';
  const isLimitedToFolder = selectedLevel === 'targeted';

  const handleSelectFull = useCallback(() => {
    // Re-selecting Full while already full/targeted is a no-op so interacting with the nested
    // checkbox or folder picker (whose clicks bubble to the card) doesn't clear the folder limit.
    if (!isFullSelected) {
      onAccessLevelChange('full');
    }
  }, [isFullSelected, onAccessLevelChange]);

  const handleLimitToggle = useCallback(
    (checked: boolean) => {
      onAccessLevelChange(checked ? 'targeted' : 'full');
    },
    [onAccessLevelChange]
  );

  const handleFolderChange = useCallback(
    (e: {target: {value: string | null}}) => {
      onAgentWorkspaceFolderChange(e.target.value);
    },
    [onAgentWorkspaceFolderChange]
  );

  const getCardClassName = (selected: boolean): string => cx(classes.card, selected && classes.selectedCard);

  return (
    <Stack spacing="sm" pb="xs">
      <RoutineFieldHeader title="Agent mode" description="Control what this Agent can do when it runs." />
      <Radio.Group value={selectedLevel === 'default' ? 'default' : 'full'}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value="default"
            label="Default"
            description="This agent can research, but can't edit Benchling objects or share outputs."
            disabled={false}
            showTooltip={false}
            onSelect={() => {
              onAccessLevelChange('default');
            }}
            cardClassName={getCardClassName(selectedLevel === 'default')}
          />
          <SelectableRadioCard
            value="full"
            label="Full actions"
            description="The agent can edit Benchling objects and automatically share outputs."
            disabled={!fullAccessEnabled}
            showTooltip={false}
            rightSection={
              fullAccessEnabled ? undefined : (
                <Tooltip label={FULL_ACCESS_LOCKED_TOOLTIP} withinPortal={true} zIndex={ZINDEX_FLOATING_UI}>
                  <Box className={classes.lockIcon}>
                    <BenchlingIcon icon="lock" />
                  </Box>
                </Tooltip>
              )
            }
            onSelect={handleSelectFull}
            cardClassName={getCardClassName(isFullSelected)}
          >
            {fullAccessEnabled && isFullSelected && (
              <>
                <Divider />
                <Checkbox
                  label="Limit to a destination folder"
                  checked={isLimitedToFolder}
                  onChange={(e) => {
                    handleLimitToggle(e.currentTarget.checked);
                  }}
                />
                {isLimitedToFolder && (
                  <Box className={classes.folderSelectorWrapper}>
                    <FolderSelector
                      allowCreateFolder={true}
                      enableRedesign={true}
                      noFolderText="Select destination folder"
                      showInventory={true}
                      showNotebook={true}
                      value={agentWorkspaceFolderId === '' ? null : agentWorkspaceFolderId}
                      onChange={handleFolderChange}
                      dataTestAttrs={{component: 'RoutineAccessSection', element: 'destinationFolderPicker'}}
                    />
                  </Box>
                )}
              </>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
